import { ARTIFACTS_PATH, type ReviewCandidateResource, type ReviewResource } from '@studio/shared';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { EmptyState, Failure } from '../../shell/EmptyState.js';
import { Link } from '../../shell/router.js';
import { useReview } from './useReview.js';

/**
 * Every complete Candidate in one scan.
 *
 * A row says one of three things: Ready, an optional thought, or the exact technical
 * problem and the repair that settles it. Advice is dismissed here and nowhere else -
 * it leaves no record, because a creative observation that could be audited is an
 * approval step wearing another name.
 */
export function ContactSheet({ experimentId }: { experimentId: string }): ReactNode {
  const open = useReview(experimentId);
  const [dismissed, setDismissed] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.reload} />
      </section>
    );
  }
  const review = open.value;
  if (review === null) return <section className="page" aria-busy="true" />;

  const ready = review.candidates.filter((candidate) => candidate.exportReady).length;
  const fixable = review.candidates.filter((candidate) => candidate.safeFixes.some((fix) => fix.available)).length;

  async function act(candidateId: string, run: () => Promise<unknown>): Promise<void> {
    setBusy(candidateId);
    setFailure(null);
    try {
      await run();
      open.reload();
    } catch (caught) {
      setFailure(caught instanceof ApiError ? caught.message : 'Это не удалось сделать.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page contact-sheet">
      <div className="page-head">
        <div>
          <p className="eyebrow">Contact sheet · сначала просмотр, потом упаковка</p>
          <h1>Все готовые Candidate сразу.</h1>
          <p className="note">
            {`${ready} готовы · ${fixable} с исправлением в один клик · ${review.candidates.length} в этом Experiment`}
          </p>
        </div>
        <Link to={`/experiments/${experimentId}/export`} className="primary-link">
          Packing desk
        </Link>
      </div>

      {failure === null ? null : <Failure message={failure} />}

      {review.candidates.length === 0 ? (
        <EmptyState
          title="Пока нечего ревьюить."
          note="Готовые Candidate появляются здесь, как только набор завершён. Ничего не спрятано за шагом одобрения."
          action={
            <Link to={`/experiments/${experimentId}/explore`} className="primary-link">
              К Explore
            </Link>
          }
        />
      ) : (
        <ul className="sheet-rows" aria-label="Candidates">
          {review.candidates.map((candidate) => (
            <li key={candidate.candidateId}>
              <SheetRow
                experimentId={experimentId}
                candidate={candidate}
                pairEligible={review.pair.eligible}
                busy={busy === candidate.candidateId}
                dismissed={dismissed}
                onDismiss={(code) => setDismissed((entries) => [...entries, code])}
                onAct={act}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function stateLine(candidate: ReviewCandidateResource): string {
  if (candidate.exportReady) return 'Готово';
  return candidate.blockedReason ?? 'Этот Candidate пока нельзя упаковать.';
}

function SheetRow({
  experimentId,
  candidate,
  pairEligible,
  busy,
  dismissed,
  onDismiss,
  onAct,
}: {
  experimentId: string;
  candidate: ReviewCandidateResource;
  pairEligible: boolean;
  busy: boolean;
  dismissed: readonly string[];
  onDismiss: (code: string) => void;
  onAct: (candidateId: string, run: () => Promise<unknown>) => Promise<void>;
}): ReactNode {
  const advice = (candidate.check?.advice ?? []).filter((entry) => !dismissed.includes(`${candidate.candidateId}:${entry.code}`));
  const repairs = candidate.safeFixes.filter((fix) => fix.available);
  const unrepairable = (candidate.check?.failures ?? []).length > 0 && repairs.length === 0;

  return (
    <article className={candidate.exportReady ? 'sheet-row ready' : 'sheet-row blocked'} aria-label={candidate.label}>
      <div className="sheet-art">
        {candidate.previewArtifactIds.length === 0 ? (
          <span className="frame frame-blank" aria-hidden="true" />
        ) : (
          candidate.previewArtifactIds.map((artifactId, index) => (
            <img
              key={artifactId}
              className="frame"
              src={`${ARTIFACTS_PATH}/${artifactId}`}
              alt={`${candidate.label}, кадр ${index + 1}`}
              loading="lazy"
            />
          ))
        )}
      </div>

      <div className="sheet-body">
        <h3>{candidate.label}</h3>
        <p className={candidate.exportReady ? 'sheet-state ready' : 'sheet-state blocked'}>{stateLine(candidate)}</p>

        {advice.map((entry) => (
          <p className="note advice" key={entry.code}>
            {`Необязательно: ${entry.detail} `}
            <button type="button" className="ghost" onClick={() => onDismiss(`${candidate.candidateId}:${entry.code}`)}>
              {`Скрыть ${entry.code}`}
            </button>
          </p>
        ))}

        {(candidate.check?.verifications ?? []).map((entry) => (
          <p className="note verify" key={entry.code}>{`Проверьте в Play Console: ${entry.detail}`}</p>
        ))}

        {/*
          One press, one sequence. The repairs are sorted by the rules module into the
          order they have to run in - geometry, then encoding, then the byte ceiling -
          and offering them as separate actions would let an operator recompress a file
          that is about to be resized and end up with something quietly wrong.
        */}
        {repairs.length === 0 ? null : (
          <div className="repair">
            <p className="note">{`Одно нажатие применяет по порядку ${repairs.length === 1 ? '1 шаг' : `${repairs.length} шагов`}:`}</p>
            <ol className="repair-steps" aria-label={`Последовательность исправления для ${candidate.label}`}>
              {repairs.map((fix) => (
                <li key={`${fix.kind}:${fix.artifactId}`}>{fix.detail}</li>
              ))}
            </ol>
          </div>
        )}

        <div className="row-actions">
          <button
            type="button"
            className="ghost"
            disabled={busy}
            onClick={() => void onAct(candidate.candidateId, () => api.setCandidateFlags(candidate.candidateId, { keep: !candidate.keep }))}
          >
            {candidate.keep ? `Kept ${candidate.label}` : `Keep ${candidate.label}`}
          </button>

          {repairs.length === 0 ? null : (
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => void onAct(candidate.candidateId, () => api.applySafeFix(candidate.candidateId))}
            >
              {`Исправить ${candidate.label} автоматически`}
            </button>
          )}


          {!unrepairable ? null : (
            <Link to={`/experiments/${experimentId}/refine/${candidate.candidateId}`} className="secondary-link">
              {`Открыть ${candidate.label} в редакторе`}
            </Link>
          )}

          {!pairEligible ? null : (
            <>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() =>
                  void onAct(candidate.candidateId, () =>
                    api.setPlayTestPairSlot(experimentId, { slotIndex: 1, candidateId: candidate.candidateId }),
                  )
                }
              >
                {`Поставить ${candidate.label} в Play variant 1`}
              </button>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() =>
                  void onAct(candidate.candidateId, () =>
                    api.setPlayTestPairSlot(experimentId, { slotIndex: 2, candidateId: candidate.candidateId }),
                  )
                }
              >
                {`Поставить ${candidate.label} в Play variant 2`}
              </button>
            </>
          )}

          <Link to={`/experiments/${experimentId}/compare/${candidate.candidateId}`} className="secondary-link">
            {`Открыть ${candidate.label} на Play stage`}
          </Link>
        </div>

        {candidate.slotIndex === null ? null : <p className="note">{`В Play variant ${candidate.slotIndex}`}</p>}
      </div>
    </article>
  );
}

export type { ReviewResource };
